"use client";

import {
  Box,
  Card,
  CardMedia,
  Chip,
  Container,
  Divider,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Preloader from "../components/common/Preloader";
import { getCurrentDomain } from "../components/common/utils";
import BlogsContainer from "../components/containers/BlogsContainer";
import useTostify from "../hooks/useTostify";

const BlogView = ({ blog: propBlog }) => {
  const { blogSlug } = useParams();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");

  const { setNotification } = useTostify();
  const [loading, setLoading] = useState(!propBlog);
  const [blog, setBlog] = useState(propBlog || null);

  useEffect(() => {
    // If blog was passed as prop, use it
    if (propBlog) {
      setBlog(propBlog);
      return;
    }

    // Otherwise fetch the blog
    if (blogSlug) {
      setLoading(true);
      axios
        .get(`${getCurrentDomain()}/blogs/${blogSlug}`)
        .then((res) => {
          if (res.data && res.data.success) {
            setBlog(res.data.data);
          } else {
            setBlog(null);
          }
          setLoading(false);
        })
        .catch((err) => {
          setNotification({
            open: true,
            severity: "error",
            msg: "Error while fetching blog!",
          });
          setBlog(null);
          setLoading(false);
        });
    }
  }, [blogSlug, propBlog, setNotification]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Preloader />
      </Box>
    );
  }

  if (!blog) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h4">Blog not found</Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            The blog you're looking for doesn't exist or has been removed.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <BlogsContainer>
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              textAlign: "center",
              mb: 3,
              fontWeight: 600,
            }}
          >
            {blog.title}
          </Typography>

          {blog.bannerImage && (
            <Box sx={{ mb: 4, textAlign: "center" }}>
              <Card
                elevation={0}
                sx={{ borderRadius: "8px", overflow: "hidden" }}
              >
                <CardMedia
                  component="img"
                  src={blog.bannerImage}
                  alt={blog.title}
                  sx={{
                    width: "100%",
                    maxHeight: "500px",
                    objectFit: "cover",
                  }}
                />
              </Card>
            </Box>
          )}

          {blog.categories && blog.categories.length > 0 && (
            <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {blog.categories.map((category, index) => (
                <Chip
                  key={index}
                  label={category.categoryName || category}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          <Divider sx={{ mb: 4 }} />

          <Box sx={{ mb: 5 }}>
            <div dangerouslySetInnerHTML={{ __html: blog.content }} />

            {blog.coverImage && (
              <Box sx={{ my: 4, textAlign: "center" }}>
                <img
                  src={blog.coverImage || "/placeholder.svg"}
                  alt={`${blog.title} - additional content`}
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "8px",
                  }}
                />
              </Box>
            )}

            {blog.content2 && (
              <div dangerouslySetInnerHTML={{ __html: blog.content2 }} />
            )}

            {blog.coverImage2 && (
              <Box sx={{ my: 4, textAlign: "center" }}>
                <img
                  src={blog.coverImage2 || "/placeholder.svg"}
                  alt={`${blog.title} - additional content`}
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "8px",
                  }}
                />
              </Box>
            )}

            {blog.content3 && (
              <div dangerouslySetInnerHTML={{ __html: blog.content3 }} />
            )}
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Related content section */}
          {/* <Box sx={{ mt: 6 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
              You might also like
            </Typography>
            <RelatedTrips blogId={blog._id} />
          </Box> */}
        </Box>
      </Container>
    </BlogsContainer>
  );
};

export default BlogView;
